import { Archive, ArchiveRestore, BadgeCheck, ChevronLeft, ChevronRight, Eraser, RotateCcw, Search, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import useSWR from "swr";
import { ORDER_DELETE_CONFIRM_PHRASE, type OrderRecord, type OrderStatus } from "@/shared/contracts";
import { apiFetch, jsonBody, swrFetcher } from "@/web/api";
import { PageHeader } from "@/web/components/page-header";
import { StatusBadge } from "@/web/components/status-badge";
import { Button } from "@/web/components/ui/button";
import { Card, CardContent } from "@/web/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/web/components/ui/dialog";
import { Input } from "@/web/components/ui/input";
import { Label } from "@/web/components/ui/label";
import { Loading } from "@/web/components/ui/loading";
import { formatDate, formatMoney } from "@/web/lib/utils";

interface OrdersResponse {
  data: OrderRecord[];
  total: number;
  limit: number;
  page: number;
}

interface CleanableResponse {
  expired: number;
  stale_pending: number;
}

type CleanupScope = "expired" | "stale_pending";

/**
 * 是否为已支付订单（含迟到支付）。
 * 这类订单是唯一的对账凭证，删除时必须回填平台订单号做二次确认，
 * 后端也会在 DELETE 时校验同一个值。
 */
function isPaidOrder(order: OrderRecord | null) {
  return !!order && (order.status === "paid" || order.status === "late_paid");
}

const CLEANUP_COPY: Record<CleanupScope, { title: string; noun: string; hint: string }> = {
  expired: {
    title: "清理超时订单",
    noun: "已超时未支付的订单",
    hint: "这些订单超过了收银台下单时限，且始终未收到款项。",
  },
  stale_pending: {
    title: "清理失败订单",
    noun: "无付款记录的残留订单",
    hint: "这些订单停留在等待支付状态，但监控窗口早已结束，不会再被匹配到。",
  },
};

export function OrdersPage() {
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<OrderStatus | "">("");
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<CleanupScope | null>(null);
  const [busy, setBusy] = useState(false);
  const [repairTarget, setRepairTarget] = useState<OrderRecord | null>(null);
  const [repairBusy, setRepairBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<OrderRecord | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // 删除已支付订单时必须回填平台订单号（后端也校验，这里做前端防呆）
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [refundTarget, setRefundTarget] = useState<OrderRecord | null>(null);
  const [refundInfo, setRefundInfo] = useState<{ refundable_cents: number; refunded_total_cents: number; can_refund: boolean } | null>(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const path = `/admin-api/orders?page=${page}&limit=20&status=${encodeURIComponent(status)}&q=${encodeURIComponent(query)}${showArchived ? "&include_archived=1" : ""}`;
  // 退款可用性：没签交易类接口时诚实置灰，而不是让用户点了必然失败
  const { data: refundCapability } = useSWR<{ ok: boolean; reason: string }>(
    "/admin-api/orders/refund-capability",
    swrFetcher,
    { refreshInterval: 600_000, shouldRetryOnError: false },
  );
  const refundDisabled = refundCapability ? !refundCapability.ok : false;
  const refundTitle = refundCapability?.ok
    ? ""
    : `退款不可用：${refundCapability?.reason || "正在检测支付宝接口权限"}`;
  const { data, isLoading, mutate } = useSWR<OrdersResponse>(path, swrFetcher, { refreshInterval: 5_000 });
  const { data: cleanable, mutate: mutateCleanable } = useSWR<CleanableResponse>("/admin-api/orders/cleanable", swrFetcher, { refreshInterval: 5_000 });
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / 20));
  const pendingCount = scope === "expired" ? cleanable?.expired ?? 0 : cleanable?.stale_pending ?? 0;

  function search(event: FormEvent) {
    event.preventDefault();
    setPage(1);
    setQuery(queryInput.trim());
  }

  async function openRefund(order: OrderRecord) {
    setRefundTarget(order);
    setRefundInfo(null);
    setRefundReason("");
    setRefundAmount("");
    try {
      const info = await apiFetch<{ refundable_cents: number; refunded_total_cents: number; can_refund: boolean }>(
        `/admin-api/orders/${order.id}/refunds`,
      );
      setRefundInfo(info);
      if (info.refundable_cents > 0) setRefundAmount((info.refundable_cents / 100).toFixed(2));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "读取退款信息失败");
    }
  }

  async function confirmRefund() {
    if (!refundTarget) return;
    setRefundBusy(true);
    try {
      await apiFetch(`/admin-api/orders/${refundTarget.id}/refund`, {
        method: "POST",
        ...jsonBody({ amount: refundAmount, reason: refundReason }),
      });
      toast.success(`已退款 ¥${refundAmount}`);
      setRefundTarget(null);
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "退款失败");
    } finally {
      setRefundBusy(false);
    }
  }

  // 归档 = 对已支付订单的推荐做法：记录保留，仅从列表收起
  async function archive(order: OrderRecord) {
    try {
      await apiFetch(`/admin-api/orders/${order.id}/archive`, { method: "POST" });
      toast.success("订单已归档（记录仍保留）");
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "归档失败");
    }
  }

  async function unarchive(order: OrderRecord) {
    try {
      await apiFetch(`/admin-api/orders/${order.id}/unarchive`, { method: "POST" });
      toast.success("已取消归档");
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "取消归档失败");
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      // 已支付订单要带上回填的平台订单号，后端据此放行（也是唯一的删除凭证）
      const suffix = isPaidOrder(deleteTarget)
        ? `?confirm=${encodeURIComponent(deleteConfirmText.trim())}`
        : "";
      await apiFetch(`/admin-api/orders/${deleteTarget.id}${suffix}`, { method: "DELETE" });
      toast.success("订单已删除");
      setDeleteTarget(null);
      setDeleteConfirmText("");
      await Promise.all([mutate(), mutateCleanable()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "删除失败");
    } finally {
      setDeleteBusy(false);
    }
  }

  async function confirmRepair() {
    if (!repairTarget) return;
    setRepairBusy(true);
    try {
      await apiFetch(`/admin-api/orders/${repairTarget.id}/mark-paid`, { method: "POST", ...jsonBody({}) });
      toast.success("补单成功，已触发商户通知");
      setRepairTarget(null);
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "补单失败");
    } finally {
      setRepairBusy(false);
    }
  }

  async function confirmCleanup() {
    if (!scope) return;
    setBusy(true);
    try {
      const result = await apiFetch<{ deleted: number; scope: string }>("/admin-api/orders/cleanup", {
        method: "POST",
        ...jsonBody({ scope }),
      });
      toast.success(`已删除 ${result.deleted} 笔订单`);
      setScope(null);
      await Promise.all([mutate(), mutateCleanable()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "清理失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="订单"
        description="查询商户订单、支付匹配结果和通知投递记录。"
        actions={
          <>
            <Button variant="outline" onClick={() => { setShowArchived((value) => !value); setPage(1); }}>
              {showArchived ? "隐藏已归档" : "显示已归档"}
            </Button>
            <Button variant="outline" asChild>
              <a href={`/admin-api/orders/export.csv?status=${encodeURIComponent(status)}${showArchived ? "&include_archived=1" : ""}`}>导出 CSV</a>
            </Button>
            <Button variant="outline" disabled={!cleanable?.expired} onClick={() => setScope("expired")}>
              <Eraser />清理超时订单{cleanable?.expired ? ` (${cleanable.expired})` : ""}
            </Button>
            <Button variant="outline" disabled={!cleanable?.stale_pending} onClick={() => setScope("stale_pending")}>
              <Eraser />清理失败订单{cleanable?.stale_pending ? ` (${cleanable.stale_pending})` : ""}
            </Button>
          </>
        }
      />
      <Card>
        <CardContent className="pt-5">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row">
            <form className="flex flex-1 gap-2" onSubmit={search}>
              <Input value={queryInput} onChange={(event) => setQueryInput(event.target.value)} placeholder="平台订单号、商户订单号或商品名称" aria-label="搜索订单" />
              <Button type="submit" variant="outline"><Search />搜索</Button>
            </form>
            <select
              className="h-9 rounded-md border bg-transparent px-3 text-sm"
              value={status}
              onChange={(event) => { setStatus(event.target.value as OrderStatus | ""); setPage(1); }}
              aria-label="订单状态"
            >
              <option value="">全部状态</option><option value="pending">等待支付</option><option value="expired">已过期</option><option value="paid">支付成功</option><option value="late_paid">迟到支付</option>
            </select>
          </div>

          {isLoading ? <Loading /> : !data?.data.length ? <div className="py-16 text-center text-sm text-muted">没有符合条件的订单</div> : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="border-b text-xs text-muted"><tr><th className="pb-3 font-medium">平台订单号</th><th className="pb-3 font-medium">商户订单号</th><th className="pb-3 font-medium">接口</th><th className="pb-3 font-medium">金额</th><th className="pb-3 font-medium">实付</th><th className="pb-3 font-medium">状态</th><th className="pb-3 text-right font-medium">创建时间</th><th className="pb-3 text-right font-medium">操作</th></tr></thead>
                <tbody className="divide-y">
                  {data?.data.map((order) => (
                    <tr key={order.id} className="hover:bg-foreground/[0.025]">
                      <td className="py-3 font-mono text-xs"><Link className="hover:text-primary" to={`/orders/${order.id}`}>{order.trade_no}</Link></td>
                      <td className="py-3 font-mono text-xs">{order.out_trade_no}</td>
                      <td className="py-3 uppercase text-muted">{order.api_version}</td>
                      <td className="py-3 font-mono">{formatMoney(order.requested_amount_cents)}</td>
                      <td className="py-3 font-mono">{formatMoney(order.payable_amount_cents)}</td>
                      <td className="py-3"><StatusBadge status={order.status} /></td>
                      <td className="py-3 text-right text-xs text-muted">{formatDate(order.created_at)}</td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {order.archived_at ? (
                            <Button variant="outline" size="sm" onClick={() => void unarchive(order)}><ArchiveRestore />取消归档</Button>
                          ) : (
                            <>
                              {order.status === "pending" || order.status === "expired" ? (
                                <Button variant="outline" size="sm" onClick={() => setRepairTarget(order)}><BadgeCheck />补单</Button>
                              ) : null}
                              {(order.status === "paid" || order.status === "late_paid") ? (
                                <>
                                  <Button variant="outline" size="sm" disabled={order.channel !== "local" || refundDisabled} title={order.channel !== "local" ? "上游渠道订单不支持退款" : refundTitle} onClick={() => void openRefund(order)}><RotateCcw />退款</Button>
                                  <Button variant="outline" size="sm" title="记录保留，仅从列表收起" onClick={() => void archive(order)}><Archive />归档</Button>
                                </>
                              ) : null}
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => { setDeleteConfirmText(""); setDeleteTarget(order); }}
                          >
                            <Trash2 />删除
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-5 flex items-center justify-between border-t pt-4 text-sm text-muted">
            <span>共 {data?.total ?? 0} 笔</span>
            <div className="flex items-center gap-2"><Button variant="outline" size="icon" aria-label="上一页" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft /></Button><span className="min-w-16 text-center">{page} / {pages}</span><Button variant="outline" size="icon" aria-label="下一页" disabled={page >= pages} onClick={() => setPage((value) => value + 1)}><ChevronRight /></Button></div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={repairTarget !== null} onOpenChange={(open) => { if (!open && !repairBusy) setRepairTarget(null); }}>
        <DialogContent>
          <DialogTitle>确认补单</DialogTitle>
          <DialogDescription>
            将把订单 {repairTarget?.out_trade_no} 标记为「支付成功」，并立即向商户发送支付通知。
          </DialogDescription>
          <p className="mt-3 text-xs leading-5 text-muted">
            仅在你已确认收到这笔款项后操作。补单不会产生支付宝流水记录，订单状态会变为已支付，商户侧将收到正常的异步通知。
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" disabled={repairBusy} onClick={() => setRepairTarget(null)}>取消</Button>
            <Button disabled={repairBusy} onClick={confirmRepair}><BadgeCheck />{repairBusy ? "正在补单" : "确认补单"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={refundTarget !== null} onOpenChange={(open) => { if (!open && !refundBusy) setRefundTarget(null); }}>
        <DialogContent>
          <DialogTitle>退款</DialogTitle>
          <DialogDescription>
            按指定金额退款给付款人。可退金额为实付金额减去已成功退款的差额。
          </DialogDescription>

          <div className="mt-4 space-y-2 rounded-md bg-foreground/[0.03] px-4 py-3 text-xs leading-5">
            <div className="flex justify-between">
              <span className="text-muted">实付金额</span>
              <span className="font-medium tabular-nums">
                {refundTarget ? `¥${(refundTarget.payable_amount_cents / 100).toFixed(2)}` : "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">已退款</span>
              <span className="tabular-nums">
                {refundInfo ? `¥${(refundInfo.refunded_total_cents / 100).toFixed(2)}` : "读取中…"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">可退款</span>
              <span className="font-medium tabular-nums text-primary">
                {refundInfo ? `¥${(refundInfo.refundable_cents / 100).toFixed(2)}` : "读取中…"}
              </span>
            </div>
          </div>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="refund-amount">退款金额（元）</Label>
              <Input
                id="refund-amount"
                inputMode="decimal"
                value={refundAmount}
                onChange={(event) => setRefundAmount(event.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="0.00"
                className="font-mono"
              />
              {refundInfo && refundInfo.refundable_cents > 0 ? (
                <button
                  type="button"
                  className="text-xs text-muted transition-colors hover:text-foreground"
                  onClick={() => setRefundAmount((refundInfo.refundable_cents / 100).toFixed(2))}
                >
                  填入全部可退金额
                </button>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="refund-reason">退款原因（可选）</Label>
              <Input
                id="refund-reason"
                value={refundReason}
                onChange={(event) => setRefundReason(event.target.value)}
                placeholder="例如：客户申请退款"
                maxLength={200}
              />
            </div>
          </div>

          {refundInfo && !refundInfo.can_refund ? (
            <p className="mt-3 rounded-lg bg-destructive/[0.06] px-3.5 py-3 text-xs leading-5 text-destructive">
              该订单当前不可退款（上游渠道、未支付或缺少支付宝交易号）。
            </p>
          ) : null}

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" disabled={refundBusy} onClick={() => setRefundTarget(null)}>取消</Button>
            <Button
              disabled={refundBusy || !refundInfo?.can_refund || refundAmount.trim() === ""}
              onClick={confirmRefund}
            >
              <RotateCcw />{refundBusy ? "正在退款" : "确认退款"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open && !deleteBusy) { setDeleteTarget(null); setDeleteConfirmText(""); } }}>
        <DialogContent>
          <DialogTitle>确认删除订单</DialogTitle>
          <DialogDescription>
            将永久删除订单 {deleteTarget?.out_trade_no}，此操作不可恢复。
          </DialogDescription>
          {isPaidOrder(deleteTarget) ? (
            <div className="mt-3 space-y-2">
              <p className="rounded-lg bg-destructive/[0.06] px-3.5 py-3 text-xs leading-5 text-destructive">
                该订单已支付成功。删除后交易记录、支付流水关联和对账依据会一并丢失。
                如果只是想让它从列表消失，请改用「归档」—— 记录会完整保留。
              </p>
              <Label htmlFor="delete-confirm-input">请输入「{ORDER_DELETE_CONFIRM_PHRASE}」以确认</Label>
              <Input
                id="delete-confirm-input"
                value={deleteConfirmText}
                onChange={(event) => setDeleteConfirmText(event.target.value.trim())}
                placeholder={ORDER_DELETE_CONFIRM_PHRASE}
                autoComplete="off"
                spellCheck={false}
                autoFocus
              />
            </div>
          ) : (
            <p className="mt-3 text-xs leading-5 text-muted">
              与该订单关联的金额预留、通知任务和投递记录会一并删除。
            </p>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" disabled={deleteBusy} onClick={() => { setDeleteTarget(null); setDeleteConfirmText(""); }}>取消</Button>
            <Button
              variant="danger"
              disabled={deleteBusy || (isPaidOrder(deleteTarget) && deleteConfirmText.trim() !== ORDER_DELETE_CONFIRM_PHRASE)}
              onClick={confirmDelete}
            >
              <Trash2 />{deleteBusy ? "正在删除" : "确认删除"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={scope !== null} onOpenChange={(open) => { if (!open && !busy) setScope(null); }}>
        <DialogContent>
          <DialogTitle>{scope ? CLEANUP_COPY[scope].title : ""}</DialogTitle>
          <DialogDescription>
            {scope ? `将永久删除 ${pendingCount} 笔${CLEANUP_COPY[scope].noun}，此操作不可恢复。` : ""}
          </DialogDescription>
          {scope ? (
            <p className="mt-3 text-xs leading-5 text-muted">
              {CLEANUP_COPY[scope].hint}已支付成功与迟到支付的订单不会被删除，交易记录始终保留。
            </p>
          ) : null}
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" disabled={busy} onClick={() => setScope(null)}>取消</Button>
            <Button variant="danger" disabled={busy} onClick={confirmCleanup}>
              <Trash2 />{busy ? "正在清理" : "确认删除"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
